import {Component, OnInit} from '@angular/core';
import {switchMap} from 'rxjs/operators';
import {of} from 'rxjs';
import {
    AccountDisplayStatus,
    AccountState,
    BundleType,
    LastPurchasedMessageBundles,
    MessageBundle,
    MessageBundleAccount,
    MessageBundleBuyRequest,
    MessageBundlesForPurchase,
    MessageCounters,
    PrepaidFees,
    WebBusinessService,
    WebPrepaidService
} from 'web-angular-client';
import {AlertService} from '../../../sdk/service/alert.service';
import {AuthenticationService} from '../../../sdk/service/authentication.service';
import {AccountStatusUtil} from '../../../sdk/interface/accountStatusUtil';
import {ConfirmationService} from '../../../sdk/service/confirmation.service';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {ASecureForm} from '../../../sdk/interface/form/ASecureForm';
import {LayoutService} from '../../layout/layout.service';
import {SubscriptionService} from '../../../sdk/service/subscription.service';
import {environment} from '../../../../environments/environment';
import {EnvironmentService} from '../../../sdk/service/environment.service';

@Component({
    selector: 'app-message-bundles',
    templateUrl: 'message-bundles.component.html',
    styleUrls: ['./message-bundles.component.scss'],
    standalone: false
})
export class MessageBundlesComponent extends ASecureForm implements OnInit
{
    messageBundles: MessageBundle[];
    selectedMessageBundle: MessageBundle;
    bundleType: BundleType;
    showConfirmBuyDialog: boolean = false;
    messageBundleBuyRequest: MessageBundleBuyRequest = {messageBundleId: null, userEmail: ''};
    lastPurchasedMessageBundles: LastPurchasedMessageBundles;
    selectedMessageBundleAccount: MessageBundleAccount;
    showMessageBundleQrCode: boolean = false;
    prepaidFees: PrepaidFees[] = [];
    messageCounters: MessageCounters;
    private billingEmail: string;

    constructor(
        protected accountStatusUtil: AccountStatusUtil,
        private alertService: AlertService,
        private ngxLoader: NgxUiLoaderService,
        protected authenticationService: AuthenticationService,
        protected environmentService: EnvironmentService,
        private confirmationService: ConfirmationService,
        private layoutService: LayoutService,
        private subscriptionService: SubscriptionService,
        private webPrepaidService: WebPrepaidService,
        private businessService: WebBusinessService)
    {
        super(authenticationService, environmentService);
    }

    get smsBundles(): MessageBundle[]
    {
        return this.messageBundles?.filter((mb: MessageBundle) => mb.bundleType === BundleType.SMS)
            .sort((a, b) => (a.price > b.price) ? 1 : -1);
    }

    get emailsBundles(): MessageBundle[]
    {
        return this.messageBundles?.filter((mb: MessageBundle) => mb.bundleType === BundleType.EMAIL)
            .sort((a, b) => (a.price > b.price) ? 1 : -1);
    }

    get lastPurchasedSmsBundles(): MessageBundleAccount[]
    {
        return this.lastPurchasedMessageBundles?.smsBundles || [];
    }

    get hasOpenSmsBundleAccount(): boolean
    {
        return this.lastPurchasedSmsBundles?.filter(mba =>
            this.getAccountStatusUtil().isPaymentOutstanding(mba.accountState)).length !== 0;
    }

    get hasOpenEmailBundleAccount(): boolean
    {
        return this.lastPurchasedEmailBundles?.filter(mba =>
            this.getAccountStatusUtil().isPaymentOutstanding(mba.accountState)).length !== 0;
    }

    get lastPurchasedEmailBundles(): MessageBundleAccount[]
    {
        return this.lastPurchasedMessageBundles?.emailBundles || [];
    }

    get outstandingSignupFee(): PrepaidFees
    {
        return this.prepaidFees.filter(pa => pa.prepaidType === 'SIGNUP_FEE')[0] || null;
    }

    get outstandingSubscriptionFee(): PrepaidFees
    {
        return this.prepaidFees.filter(pa => pa.prepaidType === 'SUBSCRIPTION_FEE')[0] || null;
    }

    get isSubscriptionValid(): boolean
    {
        return this.subscriptionService.isSubscriptionValid;
    }

    get hasSystemError(): boolean
    {
        return !this.lastPurchasedMessageBundles;
    }

    get instanceName(): string
    {
        return environment.instanceName;
    }

    ngOnInit(): void
    {
        this.ngxLoader.start();
        this.webPrepaidService.getMessageBundles().subscribe({
            next: (messageBundleForPurchase: MessageBundlesForPurchase) =>
            {
                this.messageBundles = messageBundleForPurchase.messageBundleList;
                this.billingEmail = messageBundleForPurchase.billingEmail;
                this.ngxLoader.stop();
            },
            error: () =>
            {
                this.ngxLoader.stop();
                this.alertService.error('Failed to load message bundles', false);
            }
        });

        this.loadMessageCounters();
        setInterval(() =>
        {
            this.loadMessageCounters();
        }, 10000);

        this.loadPrepaidAccounts();
        this.layoutService.loadPrepaidServicesStatusInfo();
    }

    loadPrepaidAccounts(): void
    {
        const loadMessageBundlePurchaseHistory = this.webPrepaidService.getLastPurchasedMessageBundles()
            .pipe(switchMap((lastPurchasedMessageBundles: LastPurchasedMessageBundles) =>
            {
                this.lastPurchasedMessageBundles = lastPurchasedMessageBundles;
                return of(true);
            }));

        this.ngxLoader.start();
        this.webPrepaidService.getOutstandingPrepaidFees()
            .pipe(switchMap((prepaidFees: PrepaidFees[]) =>
            {
                this.prepaidFees = prepaidFees;
                return loadMessageBundlePurchaseHistory;
            })).subscribe({
            next: () =>
            {
                this.ngxLoader.stop();
            },
            error: (error) =>
            {
                this.ngxLoader.stop();
                this.alertService.error(error.error.errorMessage, true);
            }
        });
    }

    confirmBuyBundleDialog(messageBundle: MessageBundle)
    {
        this.selectedMessageBundle = messageBundle;
        this.messageBundleBuyRequest.messageBundleId = messageBundle.id;
        this.messageBundleBuyRequest.userEmail = this.billingEmail;
        this.showConfirmBuyDialog = true;
    }

    buyBundle(): void
    {
        this.ngxLoader.start();
        this.webPrepaidService.buyMessageBundle(this.messageBundleBuyRequest).subscribe(
            {
                next: (response) =>
                {
                    this.ngxLoader.stop();
                    if (!response.accountCreated)
                    {
                        this.alertService.error(`Failed to buy message bundle "${this.selectedMessageBundle?.name}".`);
                    }
                    else if (response.accountCreated && !response.emailSent)
                    {
                        this.alertService.info(
                            'The request to pay transaction was created. However, the email notification failed to send.');
                    }
                    else
                    {
                        this.alertService.success('The request to pay transaction was created and sent successfully.');
                    }
                    this.loadMessageBundlePurchaseHistory();
                },
                error: () =>
                {
                    this.ngxLoader.stop();
                    this.alertService.error(`Failed to buy message bundle "${this.selectedMessageBundle?.name}". 
                    Please try again later or contact your system administrator for assistance if required.`);
                }
            }
        );
        this.closeBundleDialog();
    }

    loadMessageBundlePurchaseHistory(): void
    {
        this.ngxLoader.start();
        this.webPrepaidService.getLastPurchasedMessageBundles().subscribe({
            next: (lastPurchasedMessageBundles: LastPurchasedMessageBundles) =>
            {
                this.lastPurchasedMessageBundles = lastPurchasedMessageBundles;
                this.ngxLoader.stop();
            },
            error: () =>
            {
                this.alertService.error('Failed to load message bundle purchase history.', false);
                this.ngxLoader.stop();
            }
        });
    }

    closeBundleDialog(): void
    {
        this.showConfirmBuyDialog = false;
    }

    getAccountStatusUtil(): AccountStatusUtil
    {
        return this.accountStatusUtil;
    }

    getDisplayStatus(accountState: AccountState): string
    {
        switch (accountState)
        {
            case AccountState.PAYMENT_OUTSTANDING:
                return AccountDisplayStatus.PAYMENT_OUTSTANDING.toString();
            case AccountState.PROCESSING_PAYMENT:
                return AccountDisplayStatus.PROCESSING_PAYMENT.toString();
            case AccountState.PAYMENT_COMPLETED:
            case AccountState.PARTIAL_PAYMENT_RECEIVED:
            case AccountState.PAYMENT_READY_FOR_SETTLEMENT:
            case AccountState.SETTLEMENT_PROCESSED:
                return AccountDisplayStatus.PAYMENT_COMPLETED.toString();
            case AccountState.PAYMENT_CANCELLED:
                return AccountDisplayStatus.PAYMENT_CANCELLED.toString();
            case AccountState.CANCELLED_DUE_TO_PRICING_PACKAGE_UPDATE:
                return AccountDisplayStatus.CANCELLED_DUE_TO_PRICING_PACKAGE_UPDATE.toString();
            default:
                return 'Unknown';
        }
    }

    showQrCodeDialog(messageBundleAccount: MessageBundleAccount)
    {
        this.selectedMessageBundleAccount = messageBundleAccount;
        this.showMessageBundleQrCode = true;
    }

    getPayAtNumber(qrUrl: string): string
    {
        return qrUrl.substring(qrUrl.lastIndexOf('/') + 1);
    }

    cancelMessageBundle(messageBundlesAccount: MessageBundleAccount)
    {
        this.confirmationService.confirm(
            'Cancel Message Bundle Request to Pay Transaction',
            `Are you sure you wish to cancel message bundle "${messageBundlesAccount.description}" request to pay transaction?`
        ).then((confirmed) =>
            {
                if (confirmed)
                {
                    this.ngxLoader.start();
                    this.webPrepaidService.cancelMessageBundleAccount(messageBundlesAccount.id).subscribe({
                        next: () =>
                        {
                            this.ngxLoader.stop();
                            this.alertService.success(
                                'Message bundle request to pay transaction cancelled successfully.',
                                false);
                            this.loadMessageBundlePurchaseHistory();
                        },
                        error: () =>
                        {
                            this.ngxLoader.stop();
                            this.alertService.error('Failed to cancel message bundle request to pay transaction', false);
                        }
                    });
                }
            }
        );
    }

    getMessageBundleToolTip(hasOutstandingMessageBundleAccount: boolean, type: string): string
    {
        if (this.isGuestAdminUser())
        {
            return `Guest accounts are not allowed to buy ${type} bundles.`;
        }
        else if (!this.isSubscriptionValid)
        {
            return `Please pay your subscription fee before attempting to buy ${type} bundles.`;
        }
        else if (this.hasSystemError)
        {
            return 'System config error, please contact support.';
        }
        else
        {
            return hasOutstandingMessageBundleAccount ? `Please pay or cancel any outstanding ${type} bundle request to pay transaction.` : '';
        }
    }

    onPayHereClick()
    {
        this.confirmationService.confirm(
            'Heads up!',
            'You are about to be redirected to our secure payments site.\n' +
            '\n' +
            '<b>Please do not close your browser window</b> during this process to ensure your transaction is completed successfully.',
            'OK',
            'Cancel',
            null,
            false,
            'pay-now-confirmation-modal-backdrop',
            'pay-now-confirmation-modal').then(
            (confirmed) =>
            {
                if (confirmed)
                {
                    window.open(this.selectedMessageBundleAccount.qrCode, '_blank');
                }
            });
    };

    private loadMessageCounters()
    {
        if (this.authenticationService.isLoggedIn())
        {
            this.businessService.getCounters()
                .subscribe((messageCountersDTO) =>
                {
                    this.messageCounters = messageCountersDTO;
                });
        }
    }
}